import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { projectsApis } from "../../../api/projects";

// Shared scope picker for the 3M norm masters (Material + Manpower). Value 0 is
// the organization-level master; any other value scopes the list/create/upload/
// export to that project. Options are the org's projects fetched once (large
// page). orgId is implicit (single-org app, taken from auth) — there is no org
// selector and no merged/inherited view.
export const ORG_MASTER_PROJECT_ID = 0;
export const ORG_MASTER_LABEL = "Organization (Master)";

type ProjectOption = { value: number; label: string };

const ProjectScopeSelect: React.FC<{
  // undefined = nothing chosen yet. Passing 0 with hideOrgMaster set would
  // render the literal text "0", since no option carries that value.
  value: number | undefined;
  /**
   * Hides the "Organization (Master)" option for masters that only exist at
   * project scope — execution norms hang off activities, which belong to a
   * project, so an org-level row is not a thing that can exist.
   */
  hideOrgMaster?: boolean;
  /**
   * The picked project's label rides along as an optional second argument, so
   * consumers can display the NAME (a "Trumpet HEP" tag) instead of the raw id
   * without re-fetching the project list. Handlers that ignore it stay valid.
   */
  onChange: (projectId: number, projectLabel?: string) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  placeholder?: string;
}> = ({ value, onChange, style, disabled, hideOrgMaster = false, placeholder }) => {
  const [options, setOptions] = useState<ProjectOption[]>(
    hideOrgMaster ? [] : [{ value: ORG_MASTER_PROJECT_ID, label: ORG_MASTER_LABEL }]
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await projectsApis.getProjectList({
          page: 1,
          pageSize: 100,
          searchQuery: null,
          direction: "P",
          sortOrder: "DESC",
          pageDiff: 0,
          paginationId: null,
          projectStatus: "Active",
        });
        if (!active) return;
        const list: any[] = res?.data?.list || [];
        setOptions([
          ...(hideOrgMaster
            ? []
            : [{ value: ORG_MASTER_PROJECT_ID, label: ORG_MASTER_LABEL }]),
          ...list.map((project: any) => ({
            value: Number(project?.projectId),
            label: project?.projectTitle || `Project ${project?.projectId}`,
          })),
        ]);
      } catch (error) {
        console.error("Error loading projects for scope picker:", error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [hideOrgMaster]);

  return (
    <Select
      value={value || undefined}
      placeholder={placeholder}
      onChange={(next: number) =>
        onChange(next, options.find((o) => o.value === next)?.label)
      }
      options={options}
      loading={loading}
      disabled={disabled}
      showSearch
      optionFilterProp="label"
      popupMatchSelectWidth={false}
      style={{ minWidth: 240, ...style }}
    />
  );
};

export default ProjectScopeSelect;
